import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { mutate } from "swr";
import { CheckIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { postFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import ReportDocDialog, {
  type ReportDocFormData,
} from "@/components/reports/report-doc-dialog";
import { refreshRolloutTasks, rolloutTaskLabel } from "@/lib/rollout-task";
import type { RolloutTask, Version } from "@/types/domain";

// Completing a rollout data task = submitting the report (PRD d4eefc2e):
// the button opens the shared report form locked to the task's version and
// POSTs /rollout-tasks/:id/confirm with the report; the server files it
// under the version's Reports and marks the task confirmed in one go.
// autoOpenParam: ?rolloutTask=<id> (from the Inbox row) opens the form for
// that task on mount and drops the param.
export const RolloutConfirmButton = ({
  task,
  version,
  autoOpenParam,
  className,
  variant = "outline",
}: {
  task: RolloutTask;
  version: Version;
  autoOpenParam?: boolean;
  className?: string;
  variant?: "default" | "outline";
}) => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(
    () => !!autoOpenParam && searchParams.get("rolloutTask") === task.id
  );
  useEffect(() => {
    if (!autoOpenParam || searchParams.get("rolloutTask") !== task.id) return;
    const next = new URLSearchParams(searchParams);
    next.delete("rolloutTask");
    setSearchParams(next, { replace: true });
  }, [autoOpenParam, searchParams, setSearchParams, task.id]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (data: ReportDocFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      await postFetcher(`/rollout-tasks/${task.id}/confirm`, {
        arg: {
          confirmed: true,
          reportDoc: {
            url: data.url,
            name: "",
            result: data.result,
            summary: data.summary,
          },
        },
      });
      toast({ title: `${rolloutTaskLabel(task)} 已提交` });
      setOpen(false);
      refreshRolloutTasks();
      mutate(
        (key) =>
          Array.isArray(key) &&
          (key[0] === "/reports" || key[0] === `/versions/${version.id}`),
        undefined,
        { revalidate: true }
      );
    } catch (e) {
      setError((e as { message?: string })?.message || "Submit failed.");
      toastError(toast, "提交失败", e, "Submit failed.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size="sm"
        disabled={submitting}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn("h-7 gap-1.5 px-2 text-xs", className)}
      >
        <CheckIcon className="h-3.5 w-3.5" />
        Submit report
      </Button>
      <ReportDocDialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setError(null);
        }}
        onSubmit={submit}
        version={version}
        isSubmitting={submitting}
        error={error}
      />
    </>
  );
};

export default RolloutConfirmButton;
